/**
 * SMS Configuration Component
 *
 * Allows SUPER_ADMIN and CLIENT_ADMIN users to configure SMS settings
 * including origination phone number, sender ID, and configuration set.
 */

import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  PhoneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../ui/activqms-loader";
import {
  GET_SMS_CONFIGURATION,
  UPDATE_SMS_CONFIGURATION,
  SMSConfiguration,
  UpdateSMSConfigurationInput,
} from "../../services/sms-configuration-api";

interface SMSConfigurationProps {
  tenantId: string;
  onConfigurationChange?: (config: SMSConfiguration) => void;
}

export const SMSConfigurationComponent: React.FC<SMSConfigurationProps> = ({
  tenantId,
  onConfigurationChange,
}) => {
  const [formData, setFormData] = useState<SMSConfiguration>({
    originationNumber: "",
    senderId: "activQMS",
    configurationSet: "",
    enabled: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Query current SMS configuration
  const { data, loading, error, refetch } = useQuery(GET_SMS_CONFIGURATION, {
    variables: { tenantId },
    fetchPolicy: "cache-and-network", // Always fetch from network to avoid stale cache
    onCompleted: (data) => {
      console.log("GET SMS configuration completed for tenant:", tenantId);
      console.log("GET SMS configuration data:", data);
      if (data?.getSMSConfiguration) {
        console.log("Setting form data:", data.getSMSConfiguration);
        setFormData(data.getSMSConfiguration);
      } else {
        console.log("No SMS configuration found, starting in editing mode");
        // No SMS configuration exists, start in editing mode
        setIsEditing(true);
      }
    },
  });

  // Update SMS configuration mutation
  const [updateSMSConfiguration, { loading: updating }] = useMutation(
    UPDATE_SMS_CONFIGURATION,
    {
      onCompleted: (data) => {
        if (data?.updateSMSConfiguration) {
          console.log(
            "SMS configuration updated successfully:",
            data.updateSMSConfiguration
          );
          console.log("Using tenant ID:", tenantId);
          setFormData(data.updateSMSConfiguration);
          setIsEditing(false);
          setValidationErrors({});
          onConfigurationChange?.(data.updateSMSConfiguration);

          // Add a small delay before refetch to ensure database consistency
          setTimeout(() => {
            console.log("Refetching SMS configuration for tenant:", tenantId);
            refetch();
          }, 1000);
        }
      },
      onError: (error) => {
        console.error("Failed to update SMS configuration:", error);
        setValidationErrors({
          general: error.message || "Failed to update SMS configuration",
        });
      },
    }
  );

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    // E.164 format validation
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleInputChange = (
    field: keyof SMSConfiguration,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation errors when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};

    // Validate phone number
    if (!formData.originationNumber) {
      errors.originationNumber = "Phone number is required";
    } else if (!validatePhoneNumber(formData.originationNumber)) {
      errors.originationNumber =
        "Phone number must be in E.164 format (e.g., +12345678901)";
    }

    // Validate sender ID
    if (!formData.senderId) {
      errors.senderId = "Sender ID is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      console.log("Updating SMS configuration for tenant:", tenantId);
      console.log("SMS configuration data:", formData);

      // Clean the form data to remove any GraphQL-specific fields like __typename
      const cleanSMSConfiguration = {
        originationNumber: formData.originationNumber,
        senderId: formData.senderId,
        configurationSet: formData.configurationSet,
        enabled: formData.enabled,
      };

      console.log("Clean SMS configuration data:", cleanSMSConfiguration);

      await updateSMSConfiguration({
        variables: {
          input: {
            tenantId,
            smsConfiguration: cleanSMSConfiguration,
          },
        },
      });
    } catch (error) {
      console.error("Error updating SMS configuration:", error);
    }
  };

  const handleCancel = () => {
    if (data?.getSMSConfiguration) {
      setFormData(data.getSMSConfiguration);
    }
    setIsEditing(false);
    setValidationErrors({});
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <ActivQMSLoader size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center text-red-600">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
          <span>Failed to load SMS configuration: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <PhoneIcon className="h-6 w-6 text-brand-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                SMS Configuration
              </h3>
              <p className="text-sm text-gray-600">
                Configure SMS origination number and settings for this tenant
              </p>
            </div>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-4">
        {!data?.getSMSConfiguration && !isEditing && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center text-yellow-800">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              <div>
                <h4 className="font-medium">SMS Not Configured</h4>
                <p className="text-sm mt-1">
                  SMS functionality is disabled for this tenant. Configure an
                  origination phone number to enable SMS features.
                </p>
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                >
                  Configure SMS Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {validationErrors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center text-red-700">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              <span className="text-sm">{validationErrors.general}</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Origination Number */}
          <div>
            <label
              htmlFor="originationNumber"
              className="block text-sm font-medium text-gray-700"
            >
              Origination Phone Number *
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="originationNumber"
                value={formData.originationNumber}
                onChange={(e) =>
                  handleInputChange("originationNumber", e.target.value)
                }
                disabled={!isEditing}
                placeholder="+12345678901"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm ${
                  validationErrors.originationNumber
                    ? "border-red-300 text-red-900 placeholder-red-300"
                    : "border-gray-300"
                } ${!isEditing ? "bg-gray-50 text-gray-500" : ""}`}
              />
              {validationErrors.originationNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.originationNumber}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Must be in E.164 format (e.g., +12345678901)
              </p>
            </div>
          </div>

          {/* Sender ID */}
          <div>
            <label
              htmlFor="senderId"
              className="block text-sm font-medium text-gray-700"
            >
              Sender ID *
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="senderId"
                value={formData.senderId || ""}
                onChange={(e) => handleInputChange("senderId", e.target.value)}
                disabled={!isEditing}
                placeholder="activQMS"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm ${
                  validationErrors.senderId
                    ? "border-red-300 text-red-900 placeholder-red-300"
                    : "border-gray-300"
                } ${!isEditing ? "bg-gray-50 text-gray-500" : ""}`}
              />
              {validationErrors.senderId && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.senderId}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The sender ID that will appear on SMS messages
              </p>
            </div>
          </div>

          {/* Configuration Set */}
          <div>
            <label
              htmlFor="configurationSet"
              className="block text-sm font-medium text-gray-700"
            >
              Configuration Set
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="configurationSet"
                value={formData.configurationSet || ""}
                onChange={(e) =>
                  handleInputChange("configurationSet", e.target.value)
                }
                disabled={!isEditing}
                placeholder="production-sms-config"
                className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm ${
                  !isEditing ? "bg-gray-50 text-gray-500" : ""
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional AWS SMS configuration set for delivery tracking
              </p>
            </div>
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center">
            <input
              id="enabled"
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => handleInputChange("enabled", e.target.checked)}
              disabled={!isEditing}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded disabled:opacity-50"
            />
            <label
              htmlFor="enabled"
              className="ml-2 block text-sm text-gray-900"
            >
              Enable SMS for this tenant
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        {isEditing && (
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={updating}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updating}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
            >
              {updating ? (
                <>
                  <ActivQMSLoader size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">
                SMS Configuration Notes
              </h4>
              <div className="mt-1 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    The origination number must be registered with AWS for SMS
                    sending
                  </li>
                  <li>Changes take effect immediately for new SMS messages</li>
                  <li>
                    Contact support if you need help with phone number
                    registration
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
